import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, STATUS_LABELS, STATUS_CHIP, formatDateTR } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { ScanLine, ArrowLeft, CheckCircle2, XCircle, CheckSquare, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "../lib/auth";
import BarcodeCameraScanner from "../components/BarcodeCameraScanner";

export default function ShipmentDetailPage() {
  const { id } = useParams();
  const [shipment, setShipment] = useState(null);
  const [barcode, setBarcode] = useState("");
  const [flash, setFlash] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selOrder, setSelOrder] = useState("");
  const [orderPieces, setOrderPieces] = useState([]);
  const [pickedPieces, setPickedPieces] = useState(new Set());
  const [addBusy, setAddBusy] = useState(false);
  const inputRef = useRef(null);
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get(`/shipments/${id}`);
      setShipment(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { inputRef.current?.focus(); }, [shipment]);

  const scan = async () => {
    const bc = barcode.trim();
    if (!bc) return;
    try {
      const { data } = await api.post(`/shipments/${id}/scan`, { barcode: bc });
      setLastResult({ ok: true, msg: data.message, piece: data.piece });
      setFlash("ok");
      toast.success("Başarıyla sevk edildi");
      await load();
    } catch (e) {
      const msg = formatApiError(e);
      setLastResult({ ok: false, msg });
      setFlash("err");
      toast.error(msg);
    } finally {
      setBarcode("");
      setTimeout(() => setFlash(""), 700);
      inputRef.current?.focus();
    }
  };

  const removePiece = async (pieceId, barcodeStr) => {
    if (!window.confirm(`${barcodeStr} parçası sevkiyattan çıkarılacak (HAZIR'a döner). Emin misiniz?`)) return;
    try {
      await api.post(`/shipments/${id}/remove-pieces`, { piece_ids: [pieceId] });
      toast.success("Parça sevkiyattan çıkarıldı");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openAddModal = async () => {
    setAddOpen(true);
    try {
      const { data } = await api.get("/orders");
      setOrders(data.filter(o => (o.ready || 0) - (o.shipped || 0) > 0));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (!selOrder) { setOrderPieces([]); setPickedPieces(new Set()); return; }
    (async () => {
      try {
        const { data } = await api.get(`/orders/${selOrder}`);
        const eligible = data.pieces.filter(p => p.status === "HAZIR" && !p.shipment_id);
        setOrderPieces(eligible);
        setPickedPieces(new Set(eligible.map(p => p.id)));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [selOrder]);

  const addSelected = async () => {
    if (pickedPieces.size === 0) return toast.error("En az bir parça seçin");
    setAddBusy(true);
    try {
      await api.post(`/shipments/${id}/add-pieces`, { piece_ids: [...pickedPieces] });
      toast.success(`${pickedPieces.size} parça eklendi`);
      setAddOpen(false);
      setSelOrder(""); setOrderPieces([]); setPickedPieces(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setAddBusy(false);
  };

  const finalize = async (override = false) => {
    try {
      await api.post(`/shipments/${id}/finalize`, { override });
      toast.success("Sevkiyat tamamlandı");
      load();
    } catch (e) {
      const msg = formatApiError(e);
      if (msg.includes("SEVKIYAT EKSİK") && hasRole(user, "production_manager")) {
        if (window.confirm(msg + "\n\nEksik olmasına rağmen tamamlamak istiyor musunuz?")) {
          finalize(true);
        }
      } else {
        toast.error(msg);
      }
    }
  };

  if (!shipment) return <div className="text-slate-500">Yükleniyor...</div>;

  const canScan = hasRole(user, "production_manager", "shipping_operator");
  const isDone = shipment.status === "TAMAMLANDI";
  const complete = shipment.scanned >= shipment.expected;

  return (
    <div className={flash === "ok" ? "flash-success" : flash === "err" ? "flash-error" : ""}>
      <Link to="/sevkiyatlar" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-700 mb-3">
        <ArrowLeft size={16} /> Sevkiyatlara Dön
      </Link>
      <PageHeader
        title={shipment.shipment_number}
        subtitle={`${shipment.customer_name} — ${shipment.destination}`}
        actions={
          <div className="flex gap-2 flex-wrap">
            {!isDone && canScan && (
              <button onClick={openAddModal} data-testid="add-pieces-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white font-bold uppercase text-sm">
                <Plus size={16}/> Parça / Sipariş Ekle
              </button>
            )}
            {!isDone && canScan && (
              <button onClick={() => finalize(false)} data-testid="finalize-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wide">
                <CheckSquare size={18} /> Sevkiyatı Tamamla
              </button>
            )}
          </div>
        }
      />

      {/* Counters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Counter label="Sevkiyat Tarihi" value={formatDateTR(shipment.shipment_date)} accent="bg-slate-900 text-white" />
        <Counter label="Beklenen" value={shipment.expected} accent="bg-slate-100" mono />
        <Counter label="Okutulan" value={shipment.scanned} accent={complete ? "bg-emerald-500 text-white border-emerald-600" : "bg-yellow-100 border-yellow-500"} mono />
        <Counter label="Kalan" value={shipment.remaining} accent={shipment.remaining === 0 ? "bg-emerald-100 border-emerald-500" : "bg-red-100 border-red-500 text-red-800"} mono />
      </div>

      {/* Vehicle info */}
      {(shipment.vehicle_info || shipment.container_ref || shipment.notes) && (
        <div className="card-industrial p-4 mb-6 text-sm text-slate-700 grid md:grid-cols-3 gap-3">
          {shipment.vehicle_info && <div><span className="text-slate-500 uppercase text-[10px] font-bold">Araç:</span> {shipment.vehicle_info}</div>}
          {shipment.container_ref && <div><span className="text-slate-500 uppercase text-[10px] font-bold">Konteyner:</span> <span className="font-mono">{shipment.container_ref}</span></div>}
          {shipment.notes && <div><span className="text-slate-500 uppercase text-[10px] font-bold">Not:</span> {shipment.notes}</div>}
        </div>
      )}

      {/* Scan section */}
      {!isDone && canScan && (
        <section className="card-industrial p-6 mb-6">
          <h2 className="font-display font-bold uppercase tracking-wide text-sm text-slate-500 mb-3">Yükleme Taraması</h2>
          <div className="flex gap-3 items-center">
            <ScanLine className="text-blue-700 flex-shrink-0" size={44} strokeWidth={2.5} />
            <input
              ref={inputRef}
              data-testid="ship-scan-input"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scan(); } }}
              placeholder="Barkod okutun..."
              className="scan-input flex-1"
              autoFocus
            />
            <button onClick={scan} data-testid="ship-scan-btn" className="px-6 py-6 bg-blue-700 hover:bg-blue-800 text-white font-bold text-lg uppercase">Sevk Et</button>
          </div>
          {lastResult && (
            <div className={`mt-4 p-4 border-2 flex items-center gap-3 ${lastResult.ok ? "bg-emerald-50 border-emerald-500 text-emerald-900" : "bg-red-50 border-red-500 text-red-900"}`} data-testid="ship-scan-result">
              {lastResult.ok ? <CheckCircle2 size={28} /> : <XCircle size={28} />}
              <div className="flex-1">
                <div className="font-display font-black text-xl">{lastResult.msg}</div>
                {lastResult.piece && (
                  <div className="text-sm font-mono">
                    {lastResult.piece.barcode} — {lastResult.piece.model} {lastResult.piece.size} · Parça {lastResult.piece.piece_number}/{lastResult.piece.piece_total}
                  </div>
                )}
              </div>
            </div>
          )}
          <BarcodeCameraScanner
            className="mt-4"
            onDetected={async (code) => {
              setBarcode(code);
              const bc = code.trim();
              if (!bc) return;
              try {
                const { data } = await api.post(`/shipments/${id}/scan`, { barcode: bc });
                setLastResult({ ok: true, msg: data.message, piece: data.piece });
                setFlash("ok");
                toast.success("Başarıyla sevk edildi");
                await load();
              } catch (e) {
                const msg = formatApiError(e);
                setLastResult({ ok: false, msg });
                setFlash("err");
                toast.error(msg);
              } finally {
                setBarcode("");
                setTimeout(() => setFlash(""), 700);
              }
            }}
          />
        </section>
      )}

      {/* Pieces */}
      <h2 className="font-display font-bold uppercase tracking-wide text-sm text-slate-500 mb-3">Sevkiyat İçeriği</h2>
      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-300 text-xs uppercase tracking-widest text-slate-600">
              <th className="text-left px-4 py-2">Barkod</th>
              <th className="text-left px-4 py-2">Sipariş</th>
              <th className="text-left px-4 py-2">Ürün</th>
              <th className="text-left px-4 py-2">Ölçü</th>
              <th className="text-left px-4 py-2">Parça</th>
              <th className="text-left px-4 py-2">Durum</th>
              {!isDone && canScan && <th className="w-16"></th>}
            </tr>
          </thead>
          <tbody>
            {shipment.pieces.map((p, i) => (
              <tr key={p.id} className={`border-b border-slate-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`} data-testid={`ship-piece-${p.barcode}`}>
                <td className="px-4 py-2 font-mono">{p.barcode}</td>
                <td className="px-4 py-2 font-mono">{p.order_number}</td>
                <td className="px-4 py-2">{p.category} {p.model}</td>
                <td className="px-4 py-2 font-mono">{p.size}</td>
                <td className="px-4 py-2 font-mono">{p.piece_number}/{p.piece_total}</td>
                <td className="px-4 py-2"><span className={STATUS_CHIP[p.status]}>{STATUS_LABELS[p.status]}</span></td>
                {!isDone && canScan && (
                  <td className="px-4 py-2 text-right">
                    {p.status !== "SEVK_EDILDI" && (
                      <button onClick={() => removePiece(p.id, p.barcode)}
                        data-testid={`remove-piece-${p.barcode}`}
                        title="Sevkiyattan çıkar"
                        className="inline-flex items-center justify-center w-7 h-7 border border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600">
                        <Trash2 size={12}/>
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => !addBusy && setAddOpen(false)} data-testid="add-pieces-modal">
          <div className="bg-white w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-xl">Sevkiyata Parça Ekle</h3>
              <button onClick={() => setAddOpen(false)} className="text-slate-400 hover:text-slate-900">✕</button>
            </div>
            <div className="mb-3">
              <label className="text-xs uppercase font-bold text-slate-600 mb-1 block">Sipariş Seç</label>
              <select value={selOrder} onChange={(e) => setSelOrder(e.target.value)}
                data-testid="add-order-select"
                className="w-full px-3 py-2 border-2 border-slate-300 focus:border-blue-700 outline-none">
                <option value="">— Hazır siparişlerden seçin —</option>
                {orders.map(o => <option key={o.id} value={o.id}>{o.order_number} — {o.customer_name} ({(o.ready||0)-(o.shipped||0)} hazır)</option>)}
              </select>
            </div>
            {orderPieces.length > 0 && (
              <>
                <div className="text-sm mb-2">Uygun: <b className="text-emerald-700">{orderPieces.length}</b> · Seçili: <b className="text-blue-700">{pickedPieces.size}</b></div>
                <div className="max-h-64 overflow-y-auto border border-slate-300">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0"><tr className="text-xs uppercase text-slate-600 border-b border-slate-300">
                      <th className="w-10"></th><th className="text-left px-3 py-2">Barkod</th><th className="text-left px-3 py-2">Ürün</th><th className="text-left px-3 py-2">Ölçü</th>
                    </tr></thead>
                    <tbody>
                      {orderPieces.map(p => (
                        <tr key={p.id} className="border-b border-slate-200">
                          <td className="px-3 py-1.5"><input type="checkbox" checked={pickedPieces.has(p.id)} onChange={() => {
                            const s = new Set(pickedPieces); s.has(p.id) ? s.delete(p.id) : s.add(p.id); setPickedPieces(s);
                          }} data-testid={`add-piece-${p.barcode}`}/></td>
                          <td className="px-3 py-1.5 font-mono text-xs">{p.barcode}</td>
                          <td className="px-3 py-1.5">{p.category} {p.model}</td>
                          <td className="px-3 py-1.5 font-mono">{p.size}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setAddOpen(false)} disabled={addBusy}
                className="px-5 py-2 border-2 border-slate-300 hover:border-slate-800 font-bold text-sm">İptal</button>
              <button onClick={addSelected} disabled={addBusy || pickedPieces.size === 0}
                data-testid="confirm-add-pieces"
                className="inline-flex items-center gap-2 px-5 py-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-bold uppercase text-sm">
                <Plus size={16}/> {addBusy ? "Ekleniyor..." : `${pickedPieces.size} Parçayı Ekle`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Counter({ label, value, accent, mono }) {
  return (
    <div className={`p-5 border-2 rounded-none ${accent}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className={`font-display font-black text-4xl ${mono ? "tabular-nums" : ""} mt-1`}>{value}</div>
    </div>
  );
}
